import { generateId } from './id.js';
export function createMemoryOfflineQueuePersistence() {
    let items = [];
    return {
        load: () => items,
        save: (next) => {
            items = next;
        },
    };
}
/**
 * Thrown by a `flush()` sender to signal "the connection dropped while
 * waiting for a reply — do NOT dequeue this item, and stop flushing the
 * rest of the queue" (as opposed to a settled server rejection, which DOES
 * dequeue). Keeps a single flush attempt from racing a second one started
 * by the next reconnect.
 */
export class OfflineFlushAbortError extends Error {
    constructor() {
        super('offline queue flush aborted: connection lost mid-flush');
        this.name = 'OfflineFlushAbortError';
    }
}
export class OfflineQueue {
    items = [];
    loaded = false;
    persistence;
    constructor(persistence = createMemoryOfflineQueuePersistence()) {
        this.persistence = persistence;
    }
    async ensureLoaded() {
        if (this.loaded)
            return;
        this.items = await this.persistence.load();
        this.loaded = true;
    }
    /**
     * Adds an item. Idempotent by `id`: if `id` is supplied and an item with
     * that id is already queued, the existing item is returned unchanged (no
     * duplicate enqueue) — this is the queue-level half of the idempotency
     * guarantee (the other half is the server treating a replayed `req.id` as
     * a no-op-returning-prior-result).
     */
    async enqueue(op, payload, id) {
        await this.ensureLoaded();
        const targetId = id ?? generateId();
        const existing = this.items.find((item) => item.id === targetId);
        if (existing)
            return existing;
        const item = { id: targetId, op, payload, createdAt: Date.now() };
        this.items = [...this.items, item];
        await this.persistence.save(this.items);
        return item;
    }
    async dequeue(id) {
        await this.ensureLoaded();
        const next = this.items.filter((item) => item.id !== id);
        if (next.length === this.items.length)
            return;
        this.items = next;
        await this.persistence.save(this.items);
    }
    async list() {
        await this.ensureLoaded();
        return [...this.items];
    }
    async size() {
        await this.ensureLoaded();
        return this.items.length;
    }
    /**
     * Flushes the queue **in order**, calling `sender` for each item.
     * `sender` should resolve with the server's result on success, or throw
     * on failure.
     * - Success → item dequeued, reported `{ ok: true }`.
     * - `sender` throws {@link OfflineFlushAbortError} (connection lost while
     *   waiting) → the item is left queued, the loop stops immediately, and
     *   nothing further is reported for it or any remaining item — they stay
     *   queued for the next reconnect's flush.
     * - `sender` throws anything else (a settled server rejection) → item is
     *   dequeued, reported `{ ok: false }`, and the loop continues — the
     *   offline queue is not an infinite-retry mechanism for actions the
     *   server has already judged invalid.
     */
    async flush(sender) {
        await this.ensureLoaded();
        const outcomes = [];
        for (const item of [...this.items]) {
            try {
                const result = await sender(item);
                await this.dequeue(item.id);
                outcomes.push({ ok: true, item, result });
            }
            catch (error) {
                if (error instanceof OfflineFlushAbortError)
                    break;
                await this.dequeue(item.id);
                outcomes.push({ ok: false, item, error });
            }
        }
        return outcomes;
    }
}
